/** @module @liquidefy/code-editor/global/global-storage @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  StorageEntry,
  StorageConfig,
  StorageNamespace,
  StorageBackendType,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asStorageNamespace,
  now,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL TYPES — StorageState, NamespaceConfig, StorageStats
// ═══════════════════════════════════════════════════════════════════════════════

/** Per-namespace configuration. */
export interface NamespaceConfig {
  readonly name: StorageNamespace;
  readonly encrypt: boolean;
  readonly maxSizeMb: number;
  readonly ttlMs?: number;
}

/** State for the KV storage module. */
export interface StorageState {
  readonly config: StorageConfig;
  readonly entries: Map<string, StorageEntry>;
  readonly namespaces: Map<StorageNamespace, NamespaceConfig>;
  readonly createdAt: number;
}

/** Size and count summary for namespaces. */
export interface StorageStats {
  readonly totalEntries: number;
  readonly totalSizeBytes: number;
  readonly byNamespace: Map<StorageNamespace, { entries: number; sizeBytes: number }>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 FACTORY — createStorageState
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Creates a fresh StorageState with the provided configuration.
 * @param config - Backend and namespace configuration.
 * @returns Initialized StorageState with no entries.
 */
export function createStorageState(config: StorageConfig): StorageState {
  const defaultNsConfig: NamespaceConfig = {
    name: config.namespace,
    encrypt: config.encryptionKey !== undefined,
    maxSizeMb: config.maxSizeMb,
  };

  const namespaces = new Map<StorageNamespace, NamespaceConfig>();
  namespaces.set(config.namespace, defaultNsConfig);

  return {
    config,
    entries: new Map(),
    namespaces,
    createdAt: now(),
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 CRUD — get, set, delete
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Retrieves a stored entry by namespace and key.
 * Returns a failure if the entry is expired or not found.
 * @param state - Current storage state.
 * @param namespace - Namespace to look in.
 * @param key - Entry key.
 * @returns EditorResult wrapping the StorageEntry.
 */
export function get(
  state: StorageState,
  namespace: StorageNamespace,
  key: string
): EditorResult<StorageEntry> {
  const entryKey = makeKey(namespace, key);
  const entry = state.entries.get(entryKey);

  if (!entry) {
    return err('ENTRY_NOT_FOUND', `No entry found for key "${key}" in namespace "${namespace}"`);
  }

  if (entry.expiresAt !== undefined && entry.expiresAt < now()) {
    return err('ENTRY_EXPIRED', `Entry "${key}" in namespace "${namespace}" has expired`);
  }

  return ok(entry, 'ENTRY_RETRIEVED', `Retrieved "${key}" from namespace "${namespace}"`);
}

/**
 * Stores a value under the given namespace and key.
 * Validates that the namespace exists and respects size limits.
 * @param state - Current storage state.
 * @param namespace - Target namespace.
 * @param key - Entry key.
 * @param value - Value to store.
 * @param options - Optional TTL and encryption override.
 * @returns EditorResult wrapping updated StorageState.
 */
export function set(
  state: StorageState,
  namespace: StorageNamespace,
  key: string,
  value: unknown,
  options?: { ttlMs?: number; encrypted?: boolean }
): EditorResult<StorageState> {
  const nsConfig = state.namespaces.get(namespace);
  if (!nsConfig) {
    return err('NAMESPACE_NOT_FOUND', `Namespace "${namespace}" does not exist. Create it first.`);
  }

  const expiresAt = options?.ttlMs !== undefined
    ? now() + options.ttlMs
    : nsConfig.ttlMs !== undefined
    ? now() + nsConfig.ttlMs
    : undefined;

  const entry: StorageEntry = {
    namespace,
    key,
    value,
    expiresAt,
    encrypted: options?.encrypted ?? nsConfig.encrypt,
  };

  const entryKey = makeKey(namespace, key);
  const updated = new Map(state.entries);
  updated.set(entryKey, entry);

  return ok(
    { ...state, entries: updated },
    'ENTRY_SET',
    `Set "${key}" in namespace "${namespace}"`
  );
}

/**
 * Deletes an entry from the storage state.
 * @param state - Current storage state.
 * @param namespace - Target namespace.
 * @param key - Key to delete.
 * @returns EditorResult wrapping updated StorageState.
 */
export function del(
  state: StorageState,
  namespace: StorageNamespace,
  key: string
): EditorResult<StorageState> {
  const entryKey = makeKey(namespace, key);
  if (!state.entries.has(entryKey)) {
    return err('ENTRY_NOT_FOUND', `Entry "${key}" in namespace "${namespace}" not found`);
  }

  const updated = new Map(state.entries);
  updated.delete(entryKey);

  return ok(
    { ...state, entries: updated },
    'ENTRY_DELETED',
    `Deleted "${key}" from namespace "${namespace}"`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 LISTING — list, clear
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Lists all keys in a namespace, optionally filtered by prefix.
 * @param state - Current storage state.
 * @param namespace - Target namespace.
 * @param prefix - Optional key prefix to filter by.
 * @returns Array of matching keys.
 */
export function list(
  state: StorageState,
  namespace: StorageNamespace,
  prefix?: string
): string[] {
  const nsPrefix = `${namespace}:`;
  const keys: string[] = [];

  for (const [compoundKey, entry] of state.entries.entries()) {
    if (!compoundKey.startsWith(nsPrefix)) continue;
    if (entry.expiresAt !== undefined && entry.expiresAt < now()) continue;

    const key = compoundKey.slice(nsPrefix.length);
    if (!prefix || key.startsWith(prefix)) {
      keys.push(key);
    }
  }

  return keys;
}

/**
 * Removes all entries from a namespace.
 * @param state - Current storage state.
 * @param namespace - Namespace to clear.
 * @returns EditorResult wrapping updated StorageState.
 */
export function clear(
  state: StorageState,
  namespace: StorageNamespace
): EditorResult<StorageState> {
  if (!state.namespaces.has(namespace)) {
    return err('NAMESPACE_NOT_FOUND', `Namespace "${namespace}" does not exist`);
  }

  const nsPrefix = `${namespace}:`;
  const updated = new Map(state.entries);
  for (const key of updated.keys()) {
    if (key.startsWith(nsPrefix)) updated.delete(key);
  }

  return ok(
    { ...state, entries: updated },
    'NAMESPACE_CLEARED',
    `Namespace "${namespace}" cleared`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 NAMESPACE MANAGEMENT — createNamespace
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Creates a new namespace in the storage state.
 * @param state - Current storage state.
 * @param name - Namespace name (will be branded).
 * @param nsConfig - Optional namespace-specific configuration.
 * @returns EditorResult wrapping updated StorageState.
 */
export function createNamespace(
  state: StorageState,
  name: string,
  nsConfig?: Partial<Omit<NamespaceConfig, 'name'>>
): EditorResult<StorageState> {
  const ns = asStorageNamespace(name);
  if (state.namespaces.has(ns)) {
    return err('NAMESPACE_EXISTS', `Namespace "${name}" already exists`);
  }

  const config: NamespaceConfig = {
    name: ns,
    encrypt: nsConfig?.encrypt ?? state.config.encryptionKey !== undefined,
    maxSizeMb: nsConfig?.maxSizeMb ?? state.config.maxSizeMb,
    ttlMs: nsConfig?.ttlMs,
  };

  const updated = new Map(state.namespaces);
  updated.set(ns, config);

  return ok(
    { ...state, namespaces: updated },
    'NAMESPACE_CREATED',
    `Namespace "${name}" created`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 SIZE ESTIMATION — estimateSize, getStorageStats
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Estimates the size in bytes used by a namespace (or all namespaces).
 * Uses JSON serialization length as a proxy for byte size.
 * @param state - Current storage state.
 * @param namespace - Optional namespace to measure. If omitted, measures all.
 * @returns Estimated byte count.
 */
export function estimateSize(
  state: StorageState,
  namespace?: StorageNamespace
): number {
  let total = 0;
  for (const [key, entry] of state.entries.entries()) {
    if (namespace && !key.startsWith(`${namespace}:`)) continue;
    if (entry.expiresAt !== undefined && entry.expiresAt < now()) continue;
    total += estimateEntrySize(entry);
  }
  return total;
}

/**
 * Returns a summary of entry counts and sizes grouped by namespace.
 * @param state - Current storage state.
 * @returns StorageStats.
 */
export function getStorageStats(state: StorageState): StorageStats {
  const byNamespace = new Map<StorageNamespace, { entries: number; sizeBytes: number }>();

  for (const ns of state.namespaces.keys()) {
    byNamespace.set(ns, { entries: 0, sizeBytes: 0 });
  }

  for (const [key, entry] of state.entries.entries()) {
    if (entry.expiresAt !== undefined && entry.expiresAt < now()) continue;
    const ns = entry.namespace;
    const current = byNamespace.get(ns) ?? { entries: 0, sizeBytes: 0 };
    byNamespace.set(ns, {
      entries: current.entries + 1,
      sizeBytes: current.sizeBytes + estimateEntrySize(entry),
    });
  }

  let totalEntries = 0;
  let totalSizeBytes = 0;
  for (const { entries, sizeBytes } of byNamespace.values()) {
    totalEntries += entries;
    totalSizeBytes += sizeBytes;
  }

  return { totalEntries, totalSizeBytes, byNamespace };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 EXPORT — exportNamespace
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Serializes all non-expired entries in a namespace to a JSON string.
 * @param state - Current storage state.
 * @param namespace - Namespace to export.
 * @returns JSON string representing the namespace contents.
 */
export function exportNamespace(state: StorageState, namespace: StorageNamespace): string {
  const nsPrefix = `${namespace}:`;
  const result: Record<string, unknown> = {};

  for (const [key, entry] of state.entries.entries()) {
    if (!key.startsWith(nsPrefix)) continue;
    if (entry.expiresAt !== undefined && entry.expiresAt < now()) continue;
    const shortKey = key.slice(nsPrefix.length);
    result[shortKey] = entry.value;
  }

  return JSON.stringify(result, null, 2);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/** Creates a compound key from namespace + key. */
function makeKey(namespace: StorageNamespace, key: string): string {
  return `${namespace}:${key}`;
}

/** Estimates the byte size of a storage entry via JSON serialization length. */
function estimateEntrySize(entry: StorageEntry): number {
  try {
    return JSON.stringify(entry.value).length * 2; // UTF-16 approximation
  } catch {
    return 0;
  }
}
